import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import {
  mdiBedClock,
  mdiDotsVertical,
  mdiMonitor,
  mdiPlayCircle,
  mdiStopCircleOutline,
  mdiWeb,
} from '@mdi/js';
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import LockResetIcon from '@mui/icons-material/LockReset';

import { styled, alpha } from '@mui/material/styles';
import { theme as daaasUITheme, Menu } from 'daaas-ui';

import Icon from '@mdi/react';
import { useTranslation } from 'react-i18next';

import AxiosStatic from 'axios';
import { config } from '../../Config/AppConfig';
import { useAxios } from '../Provider/AxiosProvider';
import { tAndCAcceptedRecently } from '../../utils/sessionStorage';

import { LANGUAGE } from '../../constants/global';

import {
  ForgotPasswordDialog,
  VmAutoShutdownScheduleDialog,
  TermsAndConditionsDialog,
} from './Dialogs';

const Root = styled('div')(({ theme }) => ({
  '& .iconAlign': {
    verticalAlign: 'middle',
    paddingRight: theme.spacing(1),
  },
  '& .iconBlack': {
    color: theme.palette.common.black,
  },
  '& .iconDisabled': {
    color: 'rgba(0,0,0,0.26)',
  },
  '& .tableCellShutdownSettings': {
    width: '40px',
  },
  '.MuiButtonBase-root.Mui-disabled, .MuiButtonBase-root.Mui-disabled .MuiButton-startIcon':
    {
      color: alpha(theme.palette.common.black, 0.6),
    },
  'th.MuiTableCell-head': {
    fontWeight: 700,
  },
  'th.MuiTableCell-root:first-of-type, td.MuiTableCell-root:first-of-type': {
    paddingLeft: theme.spacing(2),
  },
  'th.MuiTableCell-root:last-child, td.MuiTableCell-root:last-child': {
    paddingRight: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    'th.MuiTableCell-root:first-of-type, td.MuiTableCell-root:first-of-type': {
      paddingLeft: theme.spacing(4),
    },
    'th.MuiTableCell-root:last-child, td.MuiTableCell-root:last-child': {
      paddingRight: theme.spacing(4),
    },
    // Highlight every other row
    'tr.MuiTableRow-root:nth-of-type(odd):not(.MuiTableRow-head)': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  [theme.breakpoints.down('md')]: {
    '& .MuiTableBody-root tr.MuiTableRow-root td.MuiTableCell-root': {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    // Highlight every other VM
    'tbody.MuiTableBody-root:nth-of-type(even)': {
      backgroundColor: theme.palette.grey[100],
    },
  },
}));

// Custom hook from: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
// The difference between the setInterval hook and this useInterval custom hook is that its arguments are "dynamic",
// allowing us to set a variable time delay, and also pause the timer if needed.
function useInterval(callback, delay) {
  const savedCallback = React.useRef();

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

// Custom hook from: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
// The difference between the setInterval hook and this useInterval custom hook is that its arguments are "dynamic",
// allowing us to set a variable time delay, and also pause the timer if needed.
function useIntervalAutoShutdown(callback, delay) {
  const savedCallback = React.useRef();

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

function VmTooltip({ disabled, title, children }) {
  if (disabled) {
    return children;
  }
  return <Tooltip title={title}>{children}</Tooltip>;
}

VmTooltip.propTypes = {
  disabled: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function VmTableVdl({ dtl, rows }) {
  // Possible VM statuses:
  //    PowerState Transitional statuses: Creating, Starting, Stopping, Deallocating
  //    PowerState Final statuses: Running, Stopped, Deallocated
  //    ProvisioningState statuses: Creating, Updating, Failed, Succeeded, Deleting, Migrating
  const statusCreating = 'creating';
  const statusStarting = 'starting';
  const statusStopping = 'stopping';
  const statusDeallocating = 'deallocating';
  const statusRunning = 'running';
  const statusStopped = 'stopped';
  const statusDeallocated = 'deallocated';
  // const statusUpdating = 'updating';
  // const statusFailed = 'failed';
  // const statusSucceeded = 'succeeded';
  // const statusDeleting = 'deleting';
  // const statusMigrating = 'migrating';

  const bastionLinkNotSet = 'notset';

  const { t, i18n } = useTranslation(['vmvdl', 'common']);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const isMdScreen = windowWidth < daaasUITheme.breakpoints.values.md;
  const axios = useAxios(true);

  // The vm status will be polled every 10 seconds for any VM that's in a transitional state.
  const statusPollingDelay = 10000;
  // Used to start/pause the status-polling timer.
  const [isStatusPollingActive, setIsStatusPollingActive] =
    React.useState(false);

  // The auto-shutdown settings will be polled every 2 seconds for any VM that's had its auto-shutdown settings changed.
  const autoShutdownPollingDelay = 2000;
  // Used to start/pause the auto-shutdown-polling timer.
  const [isAutoShutdownPollingActive, setIsAutoShutdownPollingActive] =
    React.useState(false);

  const { enqueueSnackbar } = useSnackbar();

  // Function that contains logic to enable/disable the Start button based on Status.
  const setIsStartDisabled = (status) => {
    if ([statusStopped, statusDeallocated].indexOf(status) >= 0) {
      return false;
    }
    return true;
  };

  // Function that contains logic to enable/disable the Stop button based on Status.
  const setIsStopDisabled = (status) => {
    if ([statusStarting, statusRunning].indexOf(status) >= 0) {
      return false;
    }
    return true;
  };

  // Function that contains logic to enable/disable the BrowserConnect button based on Status.
  const setIsBrowserConnectDisabled = (status, bastionLink) => {
    if ([statusRunning].indexOf(status) >= 0) {
      return bastionLink.length === 0;
    }
    return true;
  };

  // Function that contains logic to setShutdownHour.
  const setShutdownHour = (shutdownEnabled, shutdownTime) => {
    if (shutdownEnabled === 'On') {
      if (i18n.language === LANGUAGE.EN) {
        // eslint-disable-next-line no-lonely-if
        if (shutdownTime.length === 4) {
          const hours = shutdownTime.substr(0, 2);
          if (hours === '00') {
            return '12';
            // eslint-disable-next-line no-else-return
          } else if (
            ['01', '02', '03', '04', '05', '06', '07', '08', '09'].includes(
              hours
            )
          ) {
            return shutdownTime.substr(1, 1);
          } else if (['10', '11', '12'].includes(hours)) {
            return hours;
          } else {
            return (parseInt(hours, 10) - 12).toString();
          }
          // eslint-disable-next-line no-else-return
        } else {
          return '';
        }
      } else {
        // i18n.language === LANGUAGE.FR
        // eslint-disable-next-line no-lonely-if
        if (shutdownTime.length === 4) {
          return shutdownTime.substr(0, 2);
          // eslint-disable-next-line no-else-return
        } else {
          return '';
        }
      }
    } else {
      return '';
    }
  };

  // Function that contains logic to setShutdownMinute.
  const setShutdownMinute = (shutdownEnabled, shutdownTime) => {
    if (shutdownEnabled === 'On') {
      if (shutdownTime.length === 4) {
        return shutdownTime.substr(2, 2);
        // eslint-disable-next-line no-else-return
      } else {
        return '';
      }
      // eslint-disable-next-line no-else-return
    } else {
      return '';
    }
  };

  // Function that contains logic to setShutdownAmPm.
  const setShutdownAmPm = (shutdownEnabled, shutdownTime) => {
    if (shutdownEnabled === 'On') {
      if (i18n.language === LANGUAGE.EN) {
        // eslint-disable-next-line no-lonely-if
        if (shutdownTime.length === 4) {
          const hours = shutdownTime.substr(0, 2);
          if (
            [
              '00',
              '01',
              '02',
              '03',
              '04',
              '05',
              '06',
              '07',
              '08',
              '09',
              '10',
              '11',
            ].includes(hours)
          ) {
            return 'AM';
            // eslint-disable-next-line no-else-return
          } else {
            return 'PM';
          }
          // eslint-disable-next-line no-else-return
        } else {
          return '';
        }
      } else {
        // i18n.language === LANGUAGE.FR
        return '';
      }
    } else {
      return '';
    }
  };

  // Function that contains logic to setTimeZone.
  const setTimeZone = (shutdownEnabled, timeZone) => {
    if (shutdownEnabled === 'On') {
      return timeZone;
      // eslint-disable-next-line no-else-return
    } else {
      return '';
    }
  };

  // Function that contains logic to setTimeZoneAbbr.
  const setTimeZoneAbbr = (shutdownEnabled, timeZone) => {
    if (shutdownEnabled === 'On') {
      const timeZones = {
        'Pacific Standard Time': `${t('vmvdl:timezonePacificAbbr')}`,
        'Mountain Standard Time': `${t('vmvdl:timezoneMountainAbbr')}`,
        'Central Standard Time': `${t('vmvdl:timezoneCentralAbbr')}`,
        'Eastern Standard Time': `${t('vmvdl:timezoneEasternAbbr')}`,
        'Atlantic Standard Time': `${t('vmvdl:timezoneAtlanticAbbr')}`,
        'Newfoundland Standard Time': `${t('vmvdl:timezoneNewfoundlandAbbr')}`,
      };
      return timeZones[timeZone];
      // eslint-disable-next-line no-else-return
    } else {
      return '';
    }
  };

  // Function that contains logic to setNotificationEnabled.
  const setNotificationEnabled = (shutdownEnabled, notificationEnabled) => {
    if (shutdownEnabled === 'On') {
      return notificationEnabled;
      // eslint-disable-next-line no-else-return
    } else {
      return null;
    }
  };

  // Function that contains logic to setEmailAddress.
  const setEmailAddress = (
    shutdownEnabled,
    notificationEnabled,
    emailAddress
  ) => {
    if (shutdownEnabled === 'On' && notificationEnabled === 'Yes') {
      return emailAddress;
      // eslint-disable-next-line no-else-return
    } else {
      return '';
    }
  };

  // Function that contains logic to setNotificationMinutesBeforeShutdown.
  const setNotificationMinutesBeforeShutdown = (
    shutdownEnabled,
    notificationEnabled,
    notificationMinutesBeforeShutdown
  ) => {
    if (shutdownEnabled === 'On' && notificationEnabled === 'Yes') {
      return notificationMinutesBeforeShutdown;
      // eslint-disable-next-line no-else-return
    } else {
      return '';
    }
  };

  // Initial state sets 'vmRows' to passed in 'rows',
  // and enable/disables start/stop/browserconnect buttons,
  // and sets isTableLoaded to true once vmRows is populated
  const [state, setState] = React.useState({
    vmRows: rows.map((el) => ({
      vmName: el.vmName,
      vmStatus: el.vmStatus,
      vmBastionLink: bastionLinkNotSet,
      isStartDisabled: setIsStartDisabled(el.vmStatus),
      isStopDisabled: setIsStopDisabled(el.vmStatus),
      isBrowserConnectDisabled: setIsBrowserConnectDisabled(
        el.vmStatus,
        bastionLinkNotSet
      ),
      shutdownEnabled: el.shutdownSettings.shutdownEnabled,
      shutdownTime: el.shutdownSettings.shutdownTime,
      shutdownHour: setShutdownHour(
        el.shutdownSettings.shutdownEnabled,
        el.shutdownSettings.shutdownTime
      ),
      shutdownMinute: setShutdownMinute(
        el.shutdownSettings.shutdownEnabled,
        el.shutdownSettings.shutdownTime
      ),
      shutdownAmPm: setShutdownAmPm(
        el.shutdownSettings.shutdownEnabled,
        el.shutdownSettings.shutdownTime
      ),
      timeZone: setTimeZone(
        el.shutdownSettings.shutdownEnabled,
        el.shutdownSettings.timeZone
      ),
      timeZoneAbbr: setTimeZoneAbbr(
        el.shutdownSettings.shutdownEnabled,
        el.shutdownSettings.timeZone
      ),
      notificationEnabled: setNotificationEnabled(
        el.shutdownSettings.shutdownEnabled,
        el.shutdownSettings.notificationEnabled
      ),
      emailAddress: setEmailAddress(
        el.shutdownSettings.shutdownEnabled,
        el.shutdownSettings.notificationEnabled,
        el.shutdownSettings.emailAddress
      ),
      notificationMinutesBeforeShutdown: setNotificationMinutesBeforeShutdown(
        el.shutdownSettings.shutdownEnabled,
        el.shutdownSettings.notificationEnabled,
        el.shutdownSettings.notificationMinutesBeforeShutdown
      ),
    })),
    isTableLoaded: true,
  });

  const [activeVmRowForVmMoreActionsMenu, setActiveVmRowForVmMoreActionsMenu] =
    React.useState({
      vmName: '',
      shutdownEnabled: null,
      shutdownTime: '',
      shutdownHour: '',
      shutdownMinute: '',
      shutdownAmPm: '',
      timeZone: '',
      timeZoneAbbr: '',
      notificationEnabled: null,
      emailAddress: '',
      notificationMinutesBeforeShutdown: '',
    });

  const [startButtonClicked, setStartButtonClicked] = React.useState({
    justClicked: false,
    vmName: '',
  });

  // This array will hold the old auto-shutdown settings for any row that had its auto-shutdown settings changed.
  // When the dialog for changing the auto-shutdown settings is closed after saving successfully, the info in this array will be used
  // to fetch the latest (just-updated) auto-shutdown settings from Azure.
  const [oldAutoShutdownSettings, setOldAutoShutdownSettings] = React.useState(
    []
  );

  React.useEffect(() => {
    if (oldAutoShutdownSettings.length === 0) {
      setIsAutoShutdownPollingActive(false);
    } else {
      setIsAutoShutdownPollingActive(true);
    }
  }, [oldAutoShutdownSettings]);

  // Update the status of the given VM to the given value.
  // Used upon Start/Stop so the button doesn't get clicked multiple times, and so the status gets updated the first time immediately
  // so that the spinner can start right away.
  const setVmStatus = (vmName, vmStatus) => {
    setState((prevState) => ({
      ...prevState,
      vmRows: prevState.vmRows.map((el) =>
        el.vmName === vmName && el.vmStatus !== vmStatus
          ? {
              ...el,
              vmStatus,
              isStartDisabled: setIsStartDisabled(vmStatus),
              isStopDisabled: setIsStopDisabled(vmStatus),
              isBrowserConnectDisabled: setIsBrowserConnectDisabled(
                vmStatus,
                el.vmBastionLink
              ),
            }
          : el
      ),
    }));
  };

  /// set the bastion link for the row object
  const setVmBastionLink = (vmName, bastionLink) => {
    setState((prevState) => ({
      ...prevState,
      vmRows: prevState.vmRows.map((el) =>
        el.vmName === vmName
          ? {
              ...el,
              vmBastionLink: bastionLink,
              isBrowserConnectDisabled: setIsBrowserConnectDisabled(
                el.vmStatus,
                bastionLink
              ),
            }
          : el
      ),
    }));
  };

  const createSnackbar = (name, status) => {
    let statusMsg;
    switch (status) {
      case statusRunning:
        statusMsg = t('vmvdl:VMStarted');
        break;
      case statusDeallocated:
        statusMsg = t('vmvdl:VMStopped');
        break;
      default:
        statusMsg = '';
    }

    // TODO: Fix the intermittent warning in the browser console that occurs because of the enqueueSnackbar call below
    // Warning: Cannot update during an existing state transition (such as within `render`). Render methods should be a pure function of props and state.
    enqueueSnackbar(`${t('vmvdl:VM')} '${name}' ${statusMsg}.`, {
      variant: 'success',
    });
  };

  const createSnackbarForAutoShutdownSettingsSave = (vmName, variant) => {
    // eslint-disable-next-line default-case
    switch (variant) {
      case 'success':
        enqueueSnackbar(
          `${t('vmvdl:autoShutdownSettingsSavedPt1')} '${vmName}' ${t(
            'vmvdl:autoShutdownSettingsSavedSuccessfullyPt2'
          )}.`,
          {
            variant,
          }
        );
        break;
      case 'warning':
        enqueueSnackbar(
          `${t('vmvdl:autoShutdownSettingsSavedPt1')} '${vmName}' ${t(
            'vmvdl:autoShutdownSettingsNotSavedSuccessfullyPt2'
          )}.`,
          {
            variant,
          }
        );
        break;
    }
  };

  // Update the status of the given VM from Azure
  const getVmStatus = (vmName) => {
    axios
      .get(config.api.API_VM_STATUS, {
        params: { vmName, dtl },
      })
      .then((result) => {
        setState((prevState) => ({
          ...prevState,
          vmRows: prevState.vmRows.map((el) => {
            if (
              el.vmName === result.data.vm.vmName &&
              el.vmStatus !== result.data.vm.vmStatus
            ) {
              if (
                [statusRunning, statusDeallocated].includes(
                  result.data.vm.vmStatus
                )
              ) {
                createSnackbar(el.vmName, result.data.vm.vmStatus);
              }
              return {
                ...el,
                vmStatus: result.data.vm.vmStatus,
                isStartDisabled: setIsStartDisabled(result.data.vm.vmStatus),
                isStopDisabled: setIsStopDisabled(result.data.vm.vmStatus),
                isBrowserConnectDisabled: setIsBrowserConnectDisabled(
                  result.data.vm.vmStatus,
                  el.vmBastionLink
                ),
              };
            }
            return el;
          }),
        }));
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`error is: ${error}`);

        // If we have a cancellation error, it is due to missing authorization header.
        if (AxiosStatic.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.log('AxiosStatic.isCancel');
        }
      });
  };

  // Pause the timer if all of the statuses are still in a final state after fetching the latest statuses of all VMs.
  const pauseTimer = () => {
    // Update the status of all of the VMs
    axios
      .get(config.api.API_VM_LIST, { params: { dtl, inclShutdown: 'true' } })
      .then((result) => {
        const newRows = JSON.parse(
          JSON.stringify(Object.values(result.data.vmList))
        );

        for (let i = 0; i < newRows.length; i += 1) {
          setState((prevState) => ({
            ...prevState,
            vmRows: prevState.vmRows.map((el) =>
              el.vmName === newRows[i].vmName &&
              el.vmStatus !== newRows[i].vmStatus
                ? {
                    ...el,
                    vmStatus: newRows[i].vmStatus,
                    isStartDisabled: setIsStartDisabled(newRows[i].vmStatus),
                    isStopDisabled: setIsStopDisabled(newRows[i].vmStatus),
                    isBrowserConnectDisabled: setIsBrowserConnectDisabled(
                      newRows[i].vmStatus,
                      el.vmBastionLink
                    ),
                  }
                : el
            ),
          }));
        }

        // Scan the table for any transitional statuses.
        let hasTransitionalState = false;
        for (let i = 0; i < newRows.length; i += 1) {
          if (
            [
              statusCreating,
              statusStarting,
              statusStopping,
              statusDeallocating,
            ].indexOf(newRows[i].vmStatus) >= 0
          ) {
            hasTransitionalState = true;
          }
        }

        if (!hasTransitionalState) {
          setIsStatusPollingActive(false);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`error is: ${error}`);

        // If we have a cancellation error, it is due to missing authorization header.
        if (AxiosStatic.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.log('AxiosStatic.isCancel');
        }
      });
  };

  // The timer for polling the VMs that are in a transitional state.
  useInterval(
    () => {
      let hasTransitionalState = false;
      for (let i = 0; i < state.vmRows.length; i += 1) {
        if (
          [
            statusCreating,
            statusStarting,
            statusStopping,
            statusDeallocating,
          ].indexOf(state.vmRows[i].vmStatus) >= 0
        ) {
          getVmStatus(state.vmRows[i].vmName);
          hasTransitionalState = true;
        }
      }

      if (!hasTransitionalState) {
        // This code block will be entered 15 seconds after the all of the transitional statuses have been updated to final statuses.
        pauseTimer();
      }
    },
    isStatusPollingActive ? statusPollingDelay : null
  );

  // The timer for polling the VMs that had their auto-shutdown settings changed.
  useIntervalAutoShutdown(
    () => {
      if (oldAutoShutdownSettings.length > 0) {
        // Make a copy of the array oldAutoShutdownSettings and use it for the loop below, because we will be modifying the array oldAutoShutdownSettings on each iteration of the loop.
        const oldAutoShutdownSettingsCopy = oldAutoShutdownSettings.map(
          (x) => x
        );

        // Loop through each item in oldAutoShutdownSettingsCopy, get latest auto-shutdown settings.
        for (let i = 0; i < oldAutoShutdownSettingsCopy.length; i += 1) {
          const curVmName = oldAutoShutdownSettingsCopy[i].vmName;
          axios
            .post(config.api.API_VM_GET_SHUTDOWN_SETTINGS, {
              vmName: curVmName,
              dtl,
            })
            .then((result) => {
              if (result.data.vm.vmName === curVmName) {
                // the above condition should always evaluate to true

                // Put the fetched auto-shutdown settings in an object
                const newAutoShutdownSettings = {
                  shutdownEnabled:
                    result.data.vm.vmShutdownSettings.shutdownEnabled,
                  shutdownTime: result.data.vm.vmShutdownSettings.shutdownTime,
                  timeZone: setTimeZone(
                    result.data.vm.vmShutdownSettings.shutdownEnabled,
                    result.data.vm.vmShutdownSettings.timeZone
                  ),
                  notificationEnabled: setNotificationEnabled(
                    result.data.vm.vmShutdownSettings.shutdownEnabled,
                    result.data.vm.vmShutdownSettings.notificationEnabled
                  ),
                  emailAddress: setEmailAddress(
                    result.data.vm.vmShutdownSettings.shutdownEnabled,
                    result.data.vm.vmShutdownSettings.notificationEnabled,
                    result.data.vm.vmShutdownSettings.emailAddress
                  ),
                  notificationMinutesBeforeShutdown:
                    setNotificationMinutesBeforeShutdown(
                      result.data.vm.vmShutdownSettings.shutdownEnabled,
                      result.data.vm.vmShutdownSettings.notificationEnabled,
                      result.data.vm.vmShutdownSettings
                        .notificationMinutesBeforeShutdown
                    ),
                };

                // Check if any of the received values are different from the oldAutoShutdownSettingsCopy, at least one value should be different,
                // unless Azure didn't apply the auto-shutdown settings changes yet after the auto-shutdown dialog was closed after a successful update to the auto-shutdown settings.
                if (
                  newAutoShutdownSettings.shutdownEnabled !==
                    oldAutoShutdownSettingsCopy[i].shutdownEnabled ||
                  newAutoShutdownSettings.shutdownTime !==
                    oldAutoShutdownSettingsCopy[i].shutdownTime ||
                  newAutoShutdownSettings.timeZone !==
                    oldAutoShutdownSettingsCopy[i].timeZone ||
                  (newAutoShutdownSettings.notificationEnabled === null &&
                    oldAutoShutdownSettingsCopy[i].notificationEnabled !==
                      null) ||
                  (newAutoShutdownSettings.notificationEnabled !== null &&
                    oldAutoShutdownSettingsCopy[i].notificationEnabled ===
                      null) ||
                  newAutoShutdownSettings.notificationEnabled !==
                    oldAutoShutdownSettingsCopy[i].notificationEnabled ||
                  newAutoShutdownSettings.emailAddress !==
                    oldAutoShutdownSettingsCopy[i].emailAddress ||
                  newAutoShutdownSettings.notificationMinutesBeforeShutdown !==
                    oldAutoShutdownSettingsCopy[i]
                      .notificationMinutesBeforeShutdown
                ) {
                  // Update state.vmRows with the new auto-shutdown values
                  setState((prevState) => ({
                    ...prevState,
                    vmRows: prevState.vmRows.map((el) =>
                      el.vmName === curVmName
                        ? {
                            ...el,
                            shutdownEnabled:
                              newAutoShutdownSettings.shutdownEnabled,
                            shutdownTime: newAutoShutdownSettings.shutdownTime,
                            shutdownHour: setShutdownHour(
                              newAutoShutdownSettings.shutdownEnabled,
                              newAutoShutdownSettings.shutdownTime
                            ),
                            shutdownMinute: setShutdownMinute(
                              newAutoShutdownSettings.shutdownEnabled,
                              newAutoShutdownSettings.shutdownTime
                            ),
                            shutdownAmPm: setShutdownAmPm(
                              newAutoShutdownSettings.shutdownEnabled,
                              newAutoShutdownSettings.shutdownTime
                            ),
                            timeZone: setTimeZone(
                              newAutoShutdownSettings.shutdownEnabled,
                              newAutoShutdownSettings.timeZone
                            ),
                            timeZoneAbbr: setTimeZoneAbbr(
                              newAutoShutdownSettings.shutdownEnabled,
                              newAutoShutdownSettings.timeZone
                            ),
                            notificationEnabled: setNotificationEnabled(
                              newAutoShutdownSettings.shutdownEnabled,
                              newAutoShutdownSettings.notificationEnabled
                            ),
                            emailAddress: setEmailAddress(
                              newAutoShutdownSettings.shutdownEnabled,
                              newAutoShutdownSettings.notificationEnabled,
                              newAutoShutdownSettings.emailAddress
                            ),
                            notificationMinutesBeforeShutdown:
                              setNotificationMinutesBeforeShutdown(
                                newAutoShutdownSettings.shutdownEnabled,
                                newAutoShutdownSettings.notificationEnabled,
                                newAutoShutdownSettings.notificationMinutesBeforeShutdown
                              ),
                          }
                        : el
                    ),
                  }));
                  // Delete item from array oldAutoShutdownSettings
                  setOldAutoShutdownSettings((prevOldAutoShutdownSettings) =>
                    prevOldAutoShutdownSettings.filter(
                      (item) => item.vmName !== curVmName
                    )
                  );
                  createSnackbarForAutoShutdownSettingsSave(
                    curVmName,
                    'success'
                  );
                } else {
                  // No changed info fetched for auto-shutdown settings
                  // Decrease autoShutdownSettingsFetchRetriesRemaining by 1, or delete item if retries was 1 already (and show snackbar saying that the new auto-shutdown settings couldn't be fetched?)
                  // eslint-disable-next-line no-lonely-if
                  if (
                    oldAutoShutdownSettingsCopy[i]
                      .autoShutdownSettingsFetchRetriesRemaining > 1
                  ) {
                    setOldAutoShutdownSettings((prevOldAutoShutdownSettings) =>
                      prevOldAutoShutdownSettings.map((item) => {
                        if (item.vmName === curVmName) {
                          return {
                            ...item,
                            autoShutdownSettingsFetchRetriesRemaining:
                              item.autoShutdownSettingsFetchRetriesRemaining -
                              1,
                          };
                          // eslint-disable-next-line no-else-return
                        } else {
                          return item;
                        }
                      })
                    );
                  } else {
                    setOldAutoShutdownSettings((prevOldAutoShutdownSettings) =>
                      prevOldAutoShutdownSettings.filter(
                        (item) => item.vmName !== curVmName
                      )
                    );
                    createSnackbarForAutoShutdownSettingsSave(
                      curVmName,
                      'warning'
                    );
                  }
                }
              }
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.log(`error is: ${error}`);

              // If we have a cancellation error, it is due to missing authorization header.
              if (AxiosStatic.isCancel(error)) {
                // eslint-disable-next-line no-console
                console.log('AxiosStatic.isCancel');
              }
            });
        }
      }
    },
    isAutoShutdownPollingActive ? autoShutdownPollingDelay : null
  );

  // Start the timer after all the data in the table is loaded
  React.useEffect(() => {
    if (state.isTableLoaded) {
      setIsStatusPollingActive(true);
    }
  }, [state.isTableLoaded]);

  // Page load
  React.useEffect(() => {
    // Detect screen size
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    window.addEventListener('ScreenOrientation', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('ScreenOrientation', handleResize);
    };
  }, []);

  const getBastionLink = (vmName) => {
    // temporarly disable the browser connect to prevent spamming
    setVmBastionLink(vmName, '');

    // call the api
    axios
      .post(config.api.API_BASTION_GET, { vmName, dtl })
      .then((result) => {
        if (result.data.success) {
          setVmBastionLink(vmName, result.data.vmBastionLink);
          window.open(result.data.vmBastionLink, '_blank');
        } else {
          setVmBastionLink(vmName, bastionLinkNotSet);
        }
      })
      .catch((error) => {
        setVmBastionLink(vmName, bastionLinkNotSet);
        // eslint-disable-next-line no-console
        console.log(`error is: ${error}`);

        // If we have a cancellation error, it is due to missing authorization header.
        if (AxiosStatic.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.log('AxiosStatic.isCancel');
        }
      });
  };

  const createBastionLink = (vmName) => {
    axios
      .post(config.api.API_BASTION_CREATE, { vmName, dtl })
      .then((result) => {
        if (
          result.data.success &&
          Object.hasOwn(result.data, 'vmBastionLink') &&
          result.data.vmBastionLink.length > 0
        ) {
          setVmBastionLink(vmName, result.data.vmBastionLink);
        } else {
          setVmBastionLink(vmName, bastionLinkNotSet);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`error is: ${error}`);

        // If we have a cancellation error, it is due to missing authorization header.
        if (AxiosStatic.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.log('AxiosStatic.isCancel');
        }
      });
  };

  const deleteBastionLink = (vmName) => {
    axios
      .post(config.api.API_BASTION_DELETE, { vmName, dtl })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`error is: ${error}`);

        // If we have a cancellation error, it is due to missing authorization header.
        if (AxiosStatic.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.log('AxiosStatic.isCancel');
        }
      });
  };

  const [openDialog, setOpenDialog] = React.useState({
    forgotPasswordDialog: false,
    vmAutoShutdownScheduleDialog: false,
    termsAndConditionsDialog: false,
  });

  const toggleDialog = (element, value) => {
    setOpenDialog((prevOpenDialog) => ({
      ...prevOpenDialog,
      [element]: value,
    }));

    if (
      (element === 'forgotPasswordDialog' ||
        element === 'vmAutoShutdownScheduleDialog') &&
      value === false
    ) {
      setActiveVmRowForVmMoreActionsMenu({
        vmName: '',
        shutdownEnabled: null,
        shutdownTime: '',
        shutdownHour: '',
        shutdownMinute: '',
        shutdownAmPm: '',
        timeZone: '',
        timeZoneAbbr: '',
        notificationEnabled: null,
        emailAddress: '',
        notificationMinutesBeforeShutdown: '',
      });
    }
  };

  const agreedToTermsAndConditions = () => {
    setVmStatus(startButtonClicked.vmName, statusStarting);
    axios
      .post(config.api.API_VM_START, { vmName: startButtonClicked.vmName, dtl })
      .then((result) => {
        if (result.data.success) {
          createBastionLink(startButtonClicked.vmName);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`error is: ${error}`);

        // If we have a cancellation error, it is due to missing authorization header.
        if (AxiosStatic.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.log('AxiosStatic.isCancel');
        }
      })
      .finally(() => {
        setIsStatusPollingActive(true);
      });
  };

  // Fix the timing issue, agreedToTermsAndConditions() was being called too quickly
  // before the startButtonClickedVmName was set, when this code was in handleButtonClickStart().
  React.useEffect(() => {
    if (startButtonClicked.justClicked && startButtonClicked.vmName) {
      setStartButtonClicked((prevState) => ({
        ...prevState,
        justClicked: false,
      }));
      if (tAndCAcceptedRecently()) {
        agreedToTermsAndConditions();
      } else {
        toggleDialog('termsAndConditionsDialog', true);
      }
    }
  }, [startButtonClicked.justClicked, startButtonClicked.vmName]);

  const handleButtonClickStart = (row) => {
    setStartButtonClicked((prevState) => ({
      ...prevState,
      justClicked: true,
      vmName: row.vmName,
    }));
  };

  const handleButtonClickStop = (row) => {
    setVmStatus(row.vmName, statusStopping);
    axios
      .post(config.api.API_VM_STOP, { vmName: row.vmName, dtl })
      .then((result) => {
        if (result.data.success) {
          deleteBastionLink(row.vmName);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`error is: ${error}`);

        // If we have a cancellation error, it is due to missing authorization header.
        if (AxiosStatic.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.log('AxiosStatic.isCancel');
        }
      })
      .finally(() => {
        setIsStatusPollingActive(true);
      });
  };

  const launchBastionLink = (row) => {
    if (
      row.vmBastionLink !== bastionLinkNotSet &&
      row.vmBastionLink.length > 0
    ) {
      window.open(row.vmBastionLink, '_blank');
    } else {
      // getting the shareable link
      getBastionLink(row.vmName);
    }
  };

  // Re-set the shutdownHour/shutdownAmPm/timeZoneAbbr to match the language when the language is changed
  React.useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      vmRows: prevState.vmRows.map((el) => {
        return {
          ...el,
          shutdownHour: setShutdownHour(el.shutdownEnabled, el.shutdownTime),
          shutdownAmPm: setShutdownAmPm(el.shutdownEnabled, el.shutdownTime),
          timeZoneAbbr: setTimeZoneAbbr(el.shutdownEnabled, el.timeZone),
        };
      }),
    }));
  }, [i18n.language]);

  const successfulAutoShutdownSettingsSave = (oldValues) => {
    // Check if the vmName already exists in the array, if yes then update that array item, else add a new array item
    if (oldAutoShutdownSettings.some((e) => e.vmName === oldValues.vmName)) {
      setOldAutoShutdownSettings((prevOldAutoShutdownSettings) =>
        prevOldAutoShutdownSettings.map((item) => {
          if (item.vmName === oldValues.vmName) {
            return {
              ...item,
              shutdownEnabled: oldValues.shutdownEnabled,
              shutdownTime: oldValues.shutdownTime,
              timeZone: oldValues.timeZone,
              notificationEnabled: oldValues.notificationEnabled,
              emailAddress: oldValues.emailAddress,
              notificationMinutesBeforeShutdown:
                oldValues.notificationMinutesBeforeShutdown,
              autoShutdownSettingsFetchRetriesRemaining: 3,
            };
            // eslint-disable-next-line no-else-return
          } else {
            return item;
          }
        })
      );
    } else {
      setOldAutoShutdownSettings((prevOldAutoShutdownSettings) => [
        ...prevOldAutoShutdownSettings,
        {
          vmName: oldValues.vmName,
          shutdownEnabled: oldValues.shutdownEnabled,
          shutdownTime: oldValues.shutdownTime,
          timeZone: oldValues.timeZone,
          notificationEnabled: oldValues.notificationEnabled,
          emailAddress: oldValues.emailAddress,
          notificationMinutesBeforeShutdown:
            oldValues.notificationMinutesBeforeShutdown,
          autoShutdownSettingsFetchRetriesRemaining: 3,
        },
      ]);
    }
  };

  const [menuAnchorForVmMoreActionsMenu, setMenuAnchorForVmMoreActionsMenu] =
    React.useState(null);

  const openMenu = (e, menu, vmRow) => {
    // openMenu is called each time the ... menu button is clicked, even if the menu is already open on any vmRow
    if (menu === 'vmMoreActionsMenu') {
      if (menuAnchorForVmMoreActionsMenu === null) {
        // the menu is currently closed for all vmRows, and has to be opened on this vmRow
        setActiveVmRowForVmMoreActionsMenu({
          vmName: vmRow.vmName,
          shutdownEnabled: vmRow.shutdownEnabled,
          shutdownTime: vmRow.shutdownTime,
          shutdownHour: vmRow.shutdownHour,
          shutdownMinute: vmRow.shutdownMinute,
          shutdownAmPm: vmRow.shutdownAmPm,
          timeZone: vmRow.timeZone,
          timeZoneAbbr: vmRow.timeZoneAbbr,
          notificationEnabled: vmRow.notificationEnabled,
          emailAddress: vmRow.emailAddress,
          notificationMinutesBeforeShutdown:
            vmRow.notificationMinutesBeforeShutdown,
        });
        setMenuAnchorForVmMoreActionsMenu(e.currentTarget);
      }
    }
  };

  const closeMenu = (menu) => {
    if (menu === 'vmMoreActionsMenu') {
      // Tried to do a setActiveVmRowForVmMoreActionsMenu back to initial values here, but since the closeMenu is also called when the dialogs are opened,
      // sometimes the dialog was opening with all blank values.  Right now, if a user clicks the ... button to openMenu, then clicks the ... button again or
      // clicks away from the menu, the closeMenu will be called, but activeVmRowForVmMoreActionsMenu will still have an old value even though there's no longer
      // an activeVmRow.  At the end of the day, there's no ill effect from having this happen though, it will just get populated with the correct value the next time
      // the ... button is clicked.
      setMenuAnchorForVmMoreActionsMenu(null);
    }
  };

  const handleAutoShutdown = () => {
    toggleDialog('vmAutoShutdownScheduleDialog', true);
  };

  const handleResetVmPassword = () => {
    toggleDialog('forgotPasswordDialog', true);
  };

  const vmMoreActionsMenuItems = () => {
    return [
      <MenuItem onClick={() => handleAutoShutdown()} key="1">
        <ListItemIcon>
          <Icon path={mdiBedClock} size={0.83} className="iconBlack" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2">
              {t('vmvdl:autoShutDownSettings')}
            </Typography>
          }
        />
      </MenuItem>,
      <Divider disabled key="2" />,
      <MenuItem onClick={() => handleResetVmPassword()} key="3">
        <ListItemIcon>
          <LockResetIcon fontSize="small" className="iconBlack" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2">{t('vmvdl:forgotPassword')}</Typography>
          }
        />
      </MenuItem>,
    ];
  };

  return (
    <Root>
      {isMdScreen ? (
        <TableContainer>
          <Table>
            <caption className="screen-reader-text">
              {t('vmvdl:listOfVMs')}
            </caption>
            {state.vmRows &&
              state.vmRows.map((row) => (
                <TableBody key={row.vmName}>
                  <TableRow>
                    <TableCell
                      align="left"
                      variant="head"
                      component="th"
                      scope="row"
                    >
                      {t('vmvdl:VMName')}
                    </TableCell>
                    <TableCell align="right">{row.vmName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      align="left"
                      variant="head"
                      component="th"
                      scope="row"
                    >
                      {t('vmvdl:status')}
                    </TableCell>
                    <TableCell align="right">{t(row.vmStatus)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      align="left"
                      variant="head"
                      component="th"
                      scope="row"
                      sx={[
                        (theme) => ({
                          borderBottom: '1px solid',
                          borderBottomColor: theme.palette.common.black,
                        }),
                      ]}
                    >
                      {t('vmvdl:actions')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={[
                        (theme) => ({
                          borderBottom: '1px solid',
                          borderBottomColor: theme.palette.common.black,
                        }),
                      ]}
                    >
                      <VmTooltip
                        title={t('vmvdl:startVMtitle')}
                        disabled={row.isStartDisabled}
                      >
                        <IconButton
                          aria-live="polite"
                          color="primary"
                          aria-label={`${t('vmvdl:startVM')} ${row.vmName}`}
                          disabled={row.isStartDisabled}
                          onClick={() => handleButtonClickStart(row)}
                        >
                          {row.vmStatus === statusStarting && (
                            <CircularProgress
                              aria-label={`${t('vmvdl:starting')} ${
                                row.vmName
                              }...`}
                              sx={[
                                (theme) => ({
                                  position: 'absolute',
                                  ml: theme.spacing(0),
                                }),
                              ]}
                            />
                          )}
                          <Icon size={1} path={mdiPlayCircle} />
                        </IconButton>
                      </VmTooltip>
                      <VmTooltip
                        title={t('vmvdl:stopVMtitle')}
                        disabled={row.isStopDisabled}
                      >
                        <IconButton
                          aria-live="polite"
                          color="primary"
                          aria-label={`${t('vmvdl:stopVM')} ${row.vmName}`}
                          disabled={row.isStopDisabled}
                          onClick={() => handleButtonClickStop(row)}
                        >
                          {(row.vmStatus === statusStopping ||
                            row.vmStatus === statusDeallocating) && (
                            <CircularProgress
                              aria-label={`${t('vmvdl:stopping')} ${
                                row.vmName
                              }...`}
                              sx={[
                                (theme) => ({
                                  position: 'absolute',
                                  ml: theme.spacing(0),
                                }),
                              ]}
                            />
                          )}
                          <Icon size={1} path={mdiStopCircleOutline} />
                        </IconButton>
                      </VmTooltip>
                      <VmTooltip
                        title={t('vmvdl:browserConnect')}
                        disabled={row.isBrowserConnectDisabled}
                      >
                        <IconButton
                          color="primary"
                          aria-label={`${t('vmvdl:browserConnect')} ${
                            row.vmName
                          }, ${t('common:newTab')}`}
                          disabled={row.isBrowserConnectDisabled}
                          onClick={() => launchBastionLink(row)}
                        >
                          {row.vmBastionLink === '' &&
                            row.vmStatus === statusRunning && (
                              <CircularProgress
                                aria-label={t('common:loading')}
                                sx={[
                                  (theme) => ({
                                    position: 'absolute',
                                    ml: theme.spacing(0),
                                  }),
                                ]}
                              />
                            )}
                          <Icon size={1} path={mdiWeb} />
                        </IconButton>
                      </VmTooltip>
                      <Tooltip title={t('vmvdl:vmMoreActionsTitle')}>
                        <IconButton
                          edge="end"
                          color="primary"
                          className="edge-end"
                          aria-controls="vmMoreActions-menu"
                          aria-haspopup="true"
                          aria-live="polite"
                          aria-expanded={Boolean(
                            menuAnchorForVmMoreActionsMenu
                          )}
                          aria-label={`${t('vmvdl:vmMoreActionsTitle')} ${
                            row.vmName
                          }`}
                          onClick={(e) => openMenu(e, 'vmMoreActionsMenu', row)}
                        >
                          <Icon size={1} path={mdiDotsVertical} />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        id="vmMoreActions-menu"
                        open={Boolean(menuAnchorForVmMoreActionsMenu)}
                        anchorEl={menuAnchorForVmMoreActionsMenu}
                        handleClose={() => closeMenu('vmMoreActionsMenu')}
                        onClick={() => closeMenu('vmMoreActionsMenu')}
                      >
                        {vmMoreActionsMenuItems()}
                      </Menu>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ))}
          </Table>
        </TableContainer>
      ) : (
        <TableContainer>
          <Table>
            <caption className="screen-reader-text">
              {t('vmvdl:listOfVMs')}
            </caption>
            <TableHead>
              <TableRow>
                <TableCell align="left">{t('vmvdl:VMName')}</TableCell>
                <TableCell align="left" />
                <TableCell align="left">{t('vmvdl:status')}</TableCell>
                <TableCell align="right">{t('vmvdl:actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.vmRows &&
                state.vmRows.map((row) => (
                  <TableRow key={row.vmName}>
                    <TableCell align="left">
                      <Icon size={1} path={mdiMonitor} className="iconAlign" />
                      {row.vmName}
                    </TableCell>
                    <TableCell className="tableCellShutdownSettings">
                      <Tooltip
                        title={
                          // eslint-disable-next-line no-nested-ternary
                          row.shutdownEnabled === 'On'
                            ? i18n.language === LANGUAGE.EN
                              ? `${t('vmvdl:vmAutoShutdownAt')} ${
                                  row.shutdownHour
                                }:${row.shutdownMinute} ${row.shutdownAmPm} ${
                                  row.timeZoneAbbr
                                }`
                              : `${t('vmvdl:vmAutoShutdownAt')} ${
                                  row.shutdownHour
                                }h${row.shutdownMinute} ${row.timeZoneAbbr}`
                            : t('vmvdl:vmAutoShutdownNotSet')
                        }
                      >
                        <IconButton
                          aria-live="polite"
                          className={
                            row.shutdownEnabled === 'On'
                              ? 'iconBlack'
                              : 'iconDisabled'
                          }
                          aria-label={
                            // eslint-disable-next-line no-nested-ternary
                            row.shutdownEnabled === 'On'
                              ? i18n.language === LANGUAGE.EN
                                ? `${t('vmvdl:vmAutoShutdownAt')} ${
                                    row.shutdownHour
                                  }:${row.shutdownMinute} ${row.shutdownAmPm} ${
                                    row.timeZoneAbbr
                                  } ${row.vmName}`
                                : `${t('vmvdl:vmAutoShutdownAt')} ${
                                    row.shutdownHour
                                  }h${row.shutdownMinute} ${row.timeZoneAbbr} ${
                                    row.vmName
                                  }`
                              : `${t('vmvdl:vmAutoShutdownNotSet')} ${
                                  row.vmName
                                }`
                          }
                        >
                          <Icon size={1} path={mdiBedClock} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="left">{t(row.vmStatus)}</TableCell>
                    <TableCell align="right">
                      <VmTooltip
                        title={t('vmvdl:startVMtitle')}
                        disabled={row.isStartDisabled}
                      >
                        <IconButton
                          aria-live="polite"
                          color="primary"
                          aria-label={`${t('vmvdl:startVM')} ${row.vmName}`}
                          disabled={row.isStartDisabled}
                          onClick={() => handleButtonClickStart(row)}
                        >
                          {row.vmStatus === statusStarting && (
                            <CircularProgress
                              aria-label={`${t('vmvdl:starting')} ${
                                row.vmName
                              }...`}
                              sx={[
                                (theme) => ({
                                  position: 'absolute',
                                  ml: theme.spacing(0),
                                }),
                              ]}
                            />
                          )}
                          <Icon size={1} path={mdiPlayCircle} />
                        </IconButton>
                      </VmTooltip>
                      <VmTooltip
                        title={t('vmvdl:stopVMtitle')}
                        disabled={row.isStopDisabled}
                      >
                        <IconButton
                          aria-live="polite"
                          color="primary"
                          aria-label={`${t('vmvdl:stopVM')} ${row.vmName}`}
                          disabled={row.isStopDisabled}
                          onClick={() => handleButtonClickStop(row)}
                        >
                          {(row.vmStatus === statusStopping ||
                            row.vmStatus === statusDeallocating) && (
                            <CircularProgress
                              aria-label={`${t('vmvdl:stopping')} ${
                                row.vmName
                              }...`}
                              sx={[
                                (theme) => ({
                                  position: 'absolute',
                                  ml: theme.spacing(0),
                                }),
                              ]}
                            />
                          )}
                          <Icon size={1} path={mdiStopCircleOutline} />
                        </IconButton>
                      </VmTooltip>
                      <Button
                        aria-label={`${t('vmvdl:browserConnect')} ${
                          row.vmName
                        }, ${t('common:newTab')}`}
                        color="primary"
                        startIcon={<Icon size={1} path={mdiWeb} />}
                        disabled={row.isBrowserConnectDisabled}
                        onClick={() => launchBastionLink(row)}
                      >
                        {row.vmBastionLink === '' &&
                          row.vmStatus === statusRunning && (
                            <LinearProgress
                              aria-label={t('common:loading')}
                              variant="indeterminate"
                              sx={[
                                (theme) => ({
                                  width: '100%',
                                  position: 'absolute',
                                  ml: theme.spacing(0),
                                  mt: theme.spacing(5),
                                }),
                              ]}
                            />
                          )}
                        {t('vmvdl:browserConnect')}
                      </Button>
                      <Tooltip title={t('vmvdl:vmMoreActionsTitle')}>
                        <IconButton
                          edge="end"
                          color="primary"
                          className="edge-end"
                          aria-controls="vmMoreActions-menu"
                          aria-haspopup="true"
                          aria-live="polite"
                          aria-expanded={Boolean(
                            menuAnchorForVmMoreActionsMenu
                          )}
                          aria-label={`${t('vmvdl:vmMoreActionsTitle')} ${
                            row.vmName
                          }`}
                          onClick={(e) => openMenu(e, 'vmMoreActionsMenu', row)}
                        >
                          <Icon size={1} path={mdiDotsVertical} />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        id="vmMoreActions-menu"
                        open={Boolean(menuAnchorForVmMoreActionsMenu)}
                        anchorEl={menuAnchorForVmMoreActionsMenu}
                        handleClose={() => closeMenu('vmMoreActionsMenu')}
                        onClick={() => closeMenu('vmMoreActionsMenu')}
                      >
                        {vmMoreActionsMenuItems()}
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Forgot Password dialog */}
      <ForgotPasswordDialog
        open={openDialog.forgotPasswordDialog}
        toggleDialog={() =>
          toggleDialog('forgotPasswordDialog', !openDialog.forgotPasswordDialog)
        }
        vmName={activeVmRowForVmMoreActionsMenu.vmName}
      />

      {/* VmAutoShutdownSchedule dialog */}
      <VmAutoShutdownScheduleDialog
        open={openDialog.vmAutoShutdownScheduleDialog}
        toggleDialog={() =>
          toggleDialog(
            'vmAutoShutdownScheduleDialog',
            !openDialog.vmAutoShutdownScheduleDialog
          )
        }
        successfulSave={() =>
          successfulAutoShutdownSettingsSave(activeVmRowForVmMoreActionsMenu)
        }
        dtl={dtl}
        vmName={activeVmRowForVmMoreActionsMenu.vmName}
        shutdownEnabled={activeVmRowForVmMoreActionsMenu.shutdownEnabled}
        shutdownHour={activeVmRowForVmMoreActionsMenu.shutdownHour}
        shutdownMinute={activeVmRowForVmMoreActionsMenu.shutdownMinute}
        shutdownAmPm={activeVmRowForVmMoreActionsMenu.shutdownAmPm}
        timeZone={activeVmRowForVmMoreActionsMenu.timeZone}
        notificationEnabled={
          activeVmRowForVmMoreActionsMenu.notificationEnabled
        }
        emailAddress={activeVmRowForVmMoreActionsMenu.emailAddress}
        notificationMinutesBeforeShutdown={
          activeVmRowForVmMoreActionsMenu.notificationMinutesBeforeShutdown
        }
      />

      {/* Terms And Conditions dialog */}
      <TermsAndConditionsDialog
        open={openDialog.termsAndConditionsDialog}
        toggleDialog={() =>
          toggleDialog(
            'termsAndConditionsDialog',
            !openDialog.termsAndConditionsDialog
          )
        }
        agreedToTermsAndConditions={() => agreedToTermsAndConditions()}
      />
    </Root>
  );
}

VmTableVdl.propTypes = {
  dtl: PropTypes.oneOf(['vDL']).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      vmName: PropTypes.string.isRequired,
      vmStatus: PropTypes.string.isRequired,
      shutdownSettings: PropTypes.shape({
        shutdownEnabled: PropTypes.string.isRequired,
        shutdownTime: PropTypes.string.isRequired,
        timeZone: PropTypes.string.isRequired,
        notificationEnabled: PropTypes.string,
        emailAddress: PropTypes.string.isRequired,
        notificationMinutesBeforeShutdown: PropTypes.string.isRequired,
      }),
    })
  ).isRequired,
};
